import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';
import { linkVariantsExperimental } from './link.experimental.styles';
import { linkVariants } from './link.styles';
import type { LinkProps } from './link.types';

/**
 * Link component for anchor-based navigation only.
 * Do not use asChild to wrap buttons — use the Button component instead.
 *
 * - When disabled: aria-disabled for accessibility, href removed to prevent navigation
 * - When asChild: child (e.g., Next.js Link) manages href internally
 */
const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      className,
      size = 'inherit',
      variant = 'standalone',
      tone = 'default',
      disabled = false,
      asChild,
      children,
      href,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'a';
    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    const variants = isExperimentalForms ? linkVariantsExperimental : linkVariants;

    return (
      <Comp
        ref={ref}
        {...(disabled ? { 'aria-disabled': true } : !asChild && href && { href })}
        data-experimental-form={isExperimentalForms}
        className={cn(variants({ variant, tone, size, disabled }), className)}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
Link.displayName = 'Link';

export { Link };
